import { Tooltip } from "antd";
import { getCurrentEnvironmentConfig, isProd } from "@/utils/environment";

interface EnvironmentBadgeProps {
  /**
   * Whether to show the badge (default: show in non-production environments)
   */
  show?: boolean;
  /**
   * Size variant of the badge
   */
  size?: "small" | "medium" | "large";
  /**
   * Position of the badge
   */
  position?: "inline" | "fixed";
  /**
   * Additional CSS classes
   */
  className?: string;
}

export const EnvironmentBadge = ({
  show = !isProd(),
  size = "medium",
  position = "inline",
  className = "",
}: EnvironmentBadgeProps) => {
  const config = getCurrentEnvironmentConfig();

  // Don't render if show is false
  if (!show) {
    return null;
  }

  // Size configurations
  const sizeClasses = {
    small: "px-2 py-1 text-xs",
    medium: "px-3 py-1.5 text-sm",
    large: "px-4 py-2 text-base",
  };

  // Position configurations
  const positionClasses = {
    inline: "",
    fixed: "fixed top-4 right-4 z-50 shadow-lg",
  };

  const badgeClasses = `
    inline-flex items-center gap-1.5 font-medium rounded-full border-2 transition-all duration-200 hover:scale-105
    ${sizeClasses[size]}
    ${positionClasses[position]}
    ${className}
  `.trim();

  const badgeStyle = {
    backgroundColor: config.bgColor,
    borderColor: config.borderColor,
    color: config.textColor,
  };
  return (
    <Tooltip
      title={
        <div className="text-center">
          <div className="font-medium">{config.description}</div>
          <div className="text-xs opacity-75 mt-1">Mode: {import.meta.env.MODE}</div>
        </div>
      }
      placement="bottom"
    >
      <div className={badgeClasses} style={badgeStyle}>
        <span className="text-base leading-none">{config.icon}</span>
        <span className="font-semibold">{config.displayName}</span>
      </div>
    </Tooltip>
  );
};

export default EnvironmentBadge;
